import { useNavigate, useLocation } from "react-router-dom";
import { Facebook, Instagram } from "lucide-react";
import { CONTACT, NAV_LINKS, LICENCE_NO, FOUNDED_YEAR } from "@/lib/site-data";
import { scrollToId } from "@/lib/useLenis";

export default function Footer() {
  const navigate = useNavigate();
  const location = useLocation();

  const go = (l) => {
    if (l.href) {
      navigate(l.href);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: l.id } });
      return;
    }
    scrollToId(l.id);
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#0A0B0A] text-[#F5F2EB] pt-20 pb-10"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-6">
            <div className="flex items-center gap-3">
              <span className="w-2.5 h-2.5 bg-[#D1492E] rotate-45" />
              <span className="font-serif text-2xl md:text-3xl">
                Planet Air<span className="italic text-[#D1492E]"> Adventure</span>
              </span>
            </div>
            <p className="mt-6 max-w-md text-[#F5F2EB]/65 font-light leading-relaxed">
              A boutique Bhutanese travel house based in Paro. Cultural
              circuits, high-Himalayan treks, tsechu festivals, private
              flights into Paro — every detail hand-crafted, on the ground.
            </p>
            <p className="mt-8 text-[10px] uppercase tracking-[0.28em] text-[#F5F2EB]/40">
              Licensed by the Department of Tourism, Bhutan · Licence No. {LICENCE_NO} · Est. {FOUNDED_YEAR}
            </p>
            <a
              href="/bhutan-tour-packages-from-india"
              onClick={(e) => { e.preventDefault(); navigate("/bhutan-tour-packages-from-india"); }}
              data-testid="footer-link-india"
              className="mt-4 inline-block text-sm text-[#F5F2EB]/70 hover:text-[#D1492E] transition-colors"
            >
              Bhutan tour packages from India →
            </a>
          </div>

          <div className="md:col-span-3">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#F5F2EB]/45 mb-5">
              Navigate
            </div>
            <ul className="space-y-3 font-serif text-lg">
              {NAV_LINKS.map((l) => (
                <li key={l.id}>
                  <a
                    href={l.href || `/#${l.id}`}
                    onClick={(e) => { e.preventDefault(); go(l); }}
                    data-testid={`footer-link-${l.id}`}
                    className="hover:text-[#D1492E] transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#F5F2EB]/45 mb-5">
              Contact
            </div>
            <ul className="space-y-3 font-serif text-lg">
              <li>
                <a
                  href={`tel:${CONTACT.phone1.replace(/\s/g, "")}`}
                  className="hover:text-[#D1492E] transition-colors"
                  data-testid="footer-phone-1"
                >
                  {CONTACT.phone1}
                </a>
              </li>
              <li>
                <a
                  href={`tel:${CONTACT.phone2.replace(/\s/g, "")}`}
                  className="hover:text-[#D1492E] transition-colors"
                  data-testid="footer-phone-2"
                >
                  {CONTACT.phone2}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${CONTACT.email}`}
                  className="hover:text-[#D1492E] transition-colors text-base break-all"
                  data-testid="footer-email"
                >
                  {CONTACT.email}
                </a>
              </li>
              <li className="text-base text-[#F5F2EB]/70">{CONTACT.location}</li>
            </ul>
            <div className="mt-6 flex items-center gap-4">
              <a
                href={CONTACT.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Planet Air Adventure on Facebook"
                data-testid="footer-facebook"
                className="flex items-center justify-center w-10 h-10 border border-[#F5F2EB]/25 text-[#F5F2EB]/75 hover:text-[#D1492E] hover:border-[#D1492E] transition-colors"
              >
                <Facebook size={16} strokeWidth={1.6} />
              </a>
              <a
                href={CONTACT.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Planet Air Adventure on Instagram"
                data-testid="footer-instagram"
                className="flex items-center justify-center w-10 h-10 border border-[#F5F2EB]/25 text-[#F5F2EB]/75 hover:text-[#D1492E] hover:border-[#D1492E] transition-colors"
              >
                <Instagram size={16} strokeWidth={1.6} />
              </a>
            </div>
          </div>
        </div>

        {/* Big brand mark */}
        <div className="mt-20 md:mt-24 border-t border-[#F5F2EB]/15 pt-8 overflow-hidden">
          <div className="font-serif text-[16vw] md:text-[13vw] leading-none tracking-tighter text-[#F5F2EB]/8 select-none pointer-events-none whitespace-nowrap">
            <em className="italic">Planet</em> Air Adventure
          </div>
        </div>

        <div className="mt-8 flex flex-col md:flex-row justify-between gap-4 text-[10px] uppercase tracking-[0.24em] text-[#F5F2EB]/45">
          <div>© {new Date().getFullYear()} Planet Air Adventure · Paro, Bhutan</div>
          <div>Designed for the ones who plan first.</div>
        </div>
      </div>
    </footer>
  );
}
